import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Share as ShareIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YoutubeIcon,
} from '@mui/icons-material';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const SocialMediaWidget = ({ shareUrl = window.location.href, shareText = "Check this out!" }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getShareLinks = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    
    switch(platform) {
      case 'facebook':
        return `https://www.facebook.com/dialog/share?app_id=your_app_id&display=popup&href=${encodedUrl}&quote=${encodedText}`;
      case 'twitter':
        return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
      case 'whatsapp':
        return `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
      case 'instagram':
        // Instagram doesn't have a direct share API, so we'll use the direct message URL
        return `https://www.instagram.com/direct/new/`;
      default:
        return shareUrl;
    }
  };

  const socialButtons = [
    { 
      icon: <FacebookRoundedIcon />, 
      color: '#1877F2',
      onClick: () => window.open(getShareLinks('facebook'), '_blank', 'width=600,height=400')
    },
    { 
      icon: <YoutubeIcon />, 
      color: '#ff0033',
      onClick: () => window.open('https://www.youtube.com/@CSISIndia', '_blank')
    },
    { 
      icon: <InstagramIcon />, 
      color: '#E4405F',
      onClick: () => {
        // Instagram doesn't support direct sharing, so we'll open the DM screen
        window.open(getShareLinks('instagram'), '_blank');
      }
    },
    { 
      icon: <LinkedInIcon />, 
      color: '#0A66C2',
      onClick: () => window.open(getShareLinks('linkedin'), '_blank', 'width=600,height=400')
    },
    { 
      icon: <WhatsAppIcon />, 
      color: '#25d366',
      onClick: () => window.open(getShareLinks('whatsapp'), '_blank')
    },
  ];

  return (
    <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50">
      <motion.div
        className="relative"
        initial={false}
        animate={isExpanded ? 'open' : 'closed'}
      >
        {/* Main button */}
        <motion.button
          className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white shadow-lg hover:bg-blue-600"
          onClick={() => setIsExpanded(!isExpanded)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            animate={{ rotate: isExpanded ? 45 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ShareIcon />
          </motion.div>
        </motion.button>

        {/* Social buttons */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              className="absolute right-0 bottom-0"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { transition: { staggerChildren: 0.07, delayChildren: 0.2 } },
                collapsed: { transition: { staggerChildren: 0.05, staggerDirection: -1 } },
              }}
            >
              {socialButtons.map((button, index) => (
                <motion.div
                  key={index}
                  className="absolute"
                  style={{ right: 0, bottom: 0 }}
                  variants={{
                    collapsed: { 
                      scale: 0,
                      opacity: 0,
                      y: -30,
                      x: -30,
                      transition: { type: 'spring', stiffness: 200, damping: 20 }
                    },
                    open: {
                      scale: 1,
                      opacity: 1,
                      y: - (index * 60 + 60),
                      x: 0,
                      transition: { type: 'spring', stiffness: 200, damping: 20 }
                    }
                  }}
                >
                  <motion.button
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110"
                    style={{ backgroundColor: button.color }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={button.onClick}
                  >
                    {button.icon}
                  </motion.button>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SocialMediaWidget;